<template>
  <div class="setup-container py-4 px-3">
    <ScrollContainer>
    <CreateOrganizationForm
      v-if="isCreateOrganizationFormVisible"
      :isSubmitting="isCreateOrganizationSubmitting"
      avatarIcon="pi pi-microphone"
      @submit="onSubmitOrganization"
      @cancel="onCancelOrganization"
    />

    <ProgramForm
      v-else-if="isCreateProgramFormVisible"
      :categoryOptions="categoriesStore.categoryOptions"
      :sensitiveCategoryOptions="categoriesStore.sensitiveCategoryOptions"
      cancelButtonLabel="Skip for now"
      :hostUserOptions="hostUserOptions"
      :canEditHostUserId="myUserStore.canEditProgramHostUser"
      @submit="onSubmitProgram"
      @cancel="onCancelProgram"
    />
    </ScrollContainer>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import {
  useMyUserStore,
  useProgramsStore,
  useCategoriesStore,
  useUsersStore,
  useRolesStore,
} from '@/stores';
import CreateOrganizationForm from '@/components/createOrganizationForm';
import ProgramForm from '@/components/programForm';
import * as api from '@/api';
import { parseMessageFromError } from '@/utils/errors';
import { generateOrganizationSkipKey, generateProgramSkipKey } from '@/utils/localStorageKeys';
import { ROUTE_HOST_PROGRAMS } from '@/router/routes';
import ScrollContainer from '@/components/scrollContainer';
import { doesOrganizationNameMatchDefault } from '@/utils/organizations';

export default {
  components: {
    CreateOrganizationForm,
    ProgramForm,
    ScrollContainer,
  },
  computed: {
    ...mapStores(
      useMyUserStore,
      useProgramsStore,
      useCategoriesStore,
      useUsersStore,
      useRolesStore,
    ),
    hostUserOptions() {
      return this.myUserStore.myUser && this.myUserStore.myUser.organization_id
        ? this.usersStore.getUserOptionsByOrganizationId(this.myUserStore.myUser.organization_id)
        : [];
    },
  },
  data() {
    return {
      isCreateOrganizationFormVisible: false,
      isCreateOrganizationSubmitting: false,

      isCreateProgramFormVisible: false,
      isCreateProgramSubmitting: false,
    };
  },
  async mounted() {
    await this.auditAccountSetup();
    await this.getOrganizationsUsers();
  },
  methods: {
    async auditAccountSetup() {
      try {
        const organizationId = this.myUserStore.myOrganization.id;

        const organizationNameIsDefault = doesOrganizationNameMatchDefault(
          this.myUserStore.myOrganizationName,
          this.myUserStore.myUser.fname,
        );

        const orgnizationSkipKey = generateOrganizationSkipKey(organizationId);
        const skipOrganizationRename = !!localStorage.getItem(orgnizationSkipKey);

        if (skipOrganizationRename || !organizationNameIsDefault) {
          this.isCreateOrganizationFormVisible = false;
        } else {
          this.isCreateOrganizationFormVisible = true;
          return;
        }

        this.isCreateOrganizationFormVisible = false;

        const programSkipKey = generateProgramSkipKey(organizationId);
        const skipProgramCreate = !!localStorage.getItem(programSkipKey);
        if (skipProgramCreate) {
          this.isCreateProgramFormVisible = false;

          this.$router.push({
            name: ROUTE_HOST_PROGRAMS,
          });
        } else {
          // check if user org has atleast 1 program
          await this.programsStore.getPrograms({
            organizationId,
          });
          const programs = this.programsStore
            .getProgramsByOrganizationId(organizationId);
          const organizationHasPrograms = programs.length > 0;

          // if not then show create program dialog
          if (!organizationHasPrograms) {
            this.isCreateProgramFormVisible = true;
            return;
          }

          this.isCreateProgramFormVisible = false;

          this.$router.push({
            name: ROUTE_HOST_PROGRAMS,
          });
        }
      } catch (error) {
        const message = parseMessageFromError(error, 'Error setting up account. Please refresh the page and try again.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
    async onSubmitOrganization({
      organizationForm,
      invitedUsers,
    }) {
      try {
        this.isCreateOrganizationSubmitting = true;
        const organizationId = this.myUserStore.myOrganization.id;

        await api.updateOrganization({
          organizationId,
          name: organizationForm.name,
        });

        if (invitedUsers.length > 0) {
          try {
            const roleId = this.rolesStore.hostAdminRole.id;

            await Promise.all(
              invitedUsers.map((user) => api.inviteUser({
                email: user.email,
                organizationId,
              }).then((invitedUser) => api.updateUser({
                userId: invitedUser.user_id,
                roleId,
              }))),
            );
          } catch (error) {
            const message = parseMessageFromError(error, 'Organization created, but failed to invite users. Please visit the organization page and re-invite users.');

            this.$toast.add({
              severity: 'error',
              detail: message,
            });
          }
        }

        // set organization skip key to prevent showing org form again
        // this is needed because we previously skipped the org form if an org name
        // was already set. The signup.php endpoint now sets the org name by default
        // so we need a condition to show the org form even if the org already has a name.
        localStorage.setItem(generateOrganizationSkipKey(this.myUserStore.myOrganization.id), true);

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully created organization',
        });

        await this.myUserStore.getMyUser();
        this.auditAccountSetup();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error processing request. Please try again.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.isCreateOrganizationSubmitting = false;
      }
    },
    async onCancelOrganization() {
      try {
        localStorage.setItem(generateOrganizationSkipKey(this.myUserStore.myOrganization.id), true);

        await this.auditAccountSetup();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error processing request. Please try again.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
    async onSubmitProgram({ programForm }) {
      try {
        this.isCreateProgramSubmitting = true;

        const targetAgeRanges = programForm.targetAgeRanges.reduce((acc, item) => {
          acc[item] = true;

          return acc;
        }, {});

        await api.createProgram({
          organizationId: this.myUserStore.myOrganization.id,
          name: programForm.name,
          description: programForm.showDescription,
          contactName: programForm.hostName,
          contactEmail: programForm.hostEmail,
          averageListeners: programForm.averageMonthlyDownloads,
          tapProgramId: programForm.tapProgramId,
          spreakerShowId: programForm.spreakerShowId,
          targetGender: programForm.targetGender,
          targetAgeRanges,
          adPreferences: programForm.adPreferences,
          categories: programForm.categories,
          blacklistedCategories: programForm.categoriesBlacklist,
          approvalBehavior: programForm.approvalBehavior,
          rssUrl: programForm.rssUrl,
          containsSensitiveContent: programForm.containsSensitiveContent,
          hostUserId: programForm.hostUserId,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully created podcast',
        });
        this.auditAccountSetup();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error adding podcast.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.isCreateProgramSubmitting = false;
      }
    },
    async onCancelProgram() {
      try {
        localStorage.setItem(generateProgramSkipKey(this.myUserStore.myOrganization.id), true);

        await this.auditAccountSetup();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error processing request. Please try again.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
    async getOrganizationsUsers() {
      try {
        await this.usersStore.getUsers({
          organizationId: this.myUserStore.myOrganization
            ? this.myUserStore.myOrganization.id
            : undefined,
        });
      } catch (error) {
        const message = parseMessageFromError(error, 'Error loading users.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.setup-container {
  height: calc(100vh - 67px);
  overflow-y: auto;
}
</style>
